/**
 * Keyframe interpolation (spec 006) — the only interpolator. The editor
 * store, the canvas, the preview and (from spec 012) the runtime call it;
 * none declares its own.
 */

import type { Keyframe, KeyframeEasing, Orientation } from '../@types';

/** The eased progress (0–1) for linear progress `t` (0–1). */
export function applyEasing(t: number, easing: KeyframeEasing | undefined): number {
  switch (easing) {
    case 'easeIn':
      return t * t;
    case 'easeOut':
      return 1 - (1 - t) * (1 - t);
    case 'easeInOut':
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    default:
      return t;
  }
}

/**
 * A GSAP-style ease name (`power1.inOut`, `ease-out`, `linear`, …) as one of
 * the four keyframe easings: the storyboard conversion and the scene
 * transitions (spec 012) use it, so every eased curve in a scene comes from
 * `applyEasing`. Unknown or absent names are `undefined` (linear).
 */
export function toKeyframeEasing(ease: string | undefined): KeyframeEasing | undefined {
  if (!ease) return undefined;
  if (ease === 'linear') return 'linear';
  if (/in-?out/i.test(ease)) return 'easeInOut';
  if (/out/i.test(ease)) return 'easeOut';
  if (/in/i.test(ease)) return 'easeIn';
  return undefined;
}

/** The animatable sprite properties (spec 006, flips by spec 038). `Keyframe.property` stays `string` for stored data. */
export type KeyframeProperty =
  | 'position.x' | 'position.y' | 'transform.scale' | 'transform.rotation' | 'opacity'
  | 'hinge.x' | 'hinge.y' | 'state' | 'transform.flipX' | 'transform.flipY';

/**
 * The properties that step (spec 038 D-3): the latest key at or before the
 * time plays, with no easing between keys, and nothing before the first key.
 * The flips are numbers (`1` flipped, `0` not) that cannot be half-mirrored.
 */
export const STEPPED_KEYFRAME_PROPERTIES: readonly KeyframeProperty[] = Object.freeze(
  ['state', 'transform.flipX', 'transform.flipY'] as KeyframeProperty[],
);

/** True for a property that steps (`STEPPED_KEYFRAME_PROPERTIES`). */
export const isSteppedProperty = (property: string): boolean =>
  (STEPPED_KEYFRAME_PROPERTIES as readonly string[]).includes(property);

const isUntagged = (kf: Keyframe) => !kf.orientation || kf.orientation === 'desktop';

/**
 * The keyframes of one property that count in an orientation, by time.
 * Portrait and landscape use their own tagged keyframes when the property has
 * any; otherwise (and in desktop) the untagged and desktop-tagged ones count.
 * The override is per property.
 */
export function candidateKeyframes(
  keyframes: readonly Keyframe[],
  property: string,
  orientation: Orientation | undefined,
): Keyframe[] {
  const ofProperty = keyframes.filter((kf) => kf.property === property);
  let chosen: Keyframe[] = [];
  if (orientation === 'portrait' || orientation === 'landscape') {
    chosen = ofProperty.filter((kf) => kf.orientation === orientation);
  }
  if (chosen.length === 0) chosen = ofProperty.filter(isUntagged);
  return chosen.sort((a, b) => a.time - b.time);
}

/**
 * The value of `property` at `tMs` in `orientation` from one track's keyframes.
 *
 * - No candidates: `undefined` (the caller uses the base value).
 * - Numbers: the first value at or before the first keyframe, the last at or
 *   after the last; in between, interpolated with the earlier keyframe's easing.
 * - Stepped properties (`STEPPED_KEYFRAME_PROPERTIES`: `state` and the
 *   flips) and any string values step: the latest keyframe at or before
 *   `tMs`, and `undefined` before the first one (the caller shows the
 *   default state or the at-rest flip).
 */
export function interpolateProperty(
  keyframes: readonly Keyframe[] | undefined,
  property: string,
  tMs: number,
  orientation?: Orientation,
): number | string | undefined {
  if (!keyframes || keyframes.length === 0) return undefined;
  const kfs = candidateKeyframes(keyframes, property, orientation);
  if (kfs.length === 0) return undefined;

  if (isSteppedProperty(property) || kfs.some((kf) => typeof kf.value === 'string')) {
    let active: Keyframe | undefined;
    for (const kf of kfs) {
      if (kf.time <= tMs) active = kf;
      else break;
    }
    return active?.value;
  }

  const first = kfs[0];
  const last = kfs[kfs.length - 1];
  if (tMs <= first.time) return first.value;
  if (tMs >= last.time) return last.value;

  for (let i = 0; i < kfs.length - 1; i++) {
    const curr = kfs[i];
    const next = kfs[i + 1];
    if (tMs >= curr.time && tMs <= next.time) {
      const span = next.time - curr.time;
      if (span <= 0) return next.value;
      const t = applyEasing((tMs - curr.time) / span, curr.easing);
      const a = curr.value as number;
      const b = next.value as number;
      return a + (b - a) * t;
    }
  }
  return undefined;
}

/**
 * The keyframe in force for a stepped property at `tMs` in
 * `orientation`: the latest candidate at or before `tMs`, or `undefined`
 * before the first (spec 033). `interpolateProperty` gives its value; this
 * gives the key itself, so a caller can tell when it began.
 */
export function activeKey(
  keyframes: readonly Keyframe[] | undefined,
  property: string,
  tMs: number,
  orientation?: Orientation,
): Keyframe | undefined {
  if (!keyframes || keyframes.length === 0) return undefined;
  let active: Keyframe | undefined;
  for (const kf of candidateKeyframes(keyframes, property, orientation)) {
    if (kf.time <= tMs) active = kf;
    else break;
  }
  return active;
}
